// Diálogo de confirmação genérico para o Turbo.
//
// Substitui o window.confirm nativo (disparado por data-turbo-confirm)
// por um modal estilizado com os tokens do Design System. Funciona
// automaticamente para qualquer link/form que use turbo_confirm: "...".
//
// Customização via data-attributes no elemento disparador:
//   data-turbo-confirm          = mensagem (obrigatório, já usado pelo Turbo)
//   data-confirm-title          = título do diálogo        (default: "Confirmar ação")
//   data-confirm-submit-label   = rótulo do botão confirmar (default: "Confirmar")
//   data-confirm-cancel-label   = rótulo do botão cancelar (default: "Cancelar")
//   data-confirm-variant        = "danger" | "primary"     (default: "primary")
//   data-confirm-icon           = nome do ícone lucide     (default: por variante)

import * as Turbo from "@hotwired/turbo"

const BUTTON_BASE =
  "inline-flex items-center justify-center gap-1.5 font-medium transition-colors duration-150 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--accent) focus-visible:ring-offset-2 " +
  "disabled:opacity-50 disabled:pointer-events-none active:translate-y-[1px] select-none whitespace-nowrap " +
  "rounded-(--radius) h-9 px-4 text-sm cursor-pointer"

const VARIANTS = {
  primary:   "bg-(--accent) text-white hover:bg-(--accent-hover)",
  danger:    "bg-(--danger) text-white hover:opacity-90",
  secondary: "bg-(--surface-alt) text-(--fg) border border-(--border) hover:bg-(--surface)"
}

const ICONS = {
  primary: "info",
  danger:  "alert-triangle"
}

function variantColor(variant) {
  return variant === "danger" ? "text-(--danger)" : "text-(--accent)"
}

function buildDialog({ title, message, submitLabel, cancelLabel, variant, icon }) {
  const root = document.createElement("div")
  root.className = "fixed inset-0 z-[60] flex items-center justify-center p-4"
  root.setAttribute("role", "dialog")
  root.setAttribute("aria-modal", "true")
  root.setAttribute("aria-labelledby", "confirm-dialog-title")

  root.innerHTML = `
    <div class="absolute inset-0 bg-black/40 transition-opacity duration-150 opacity-0"
         data-confirm-backdrop></div>
    <div class="relative w-full max-w-md bg-(--surface) rounded-(--radius-card) shadow-2xl flex flex-col
                transition-all duration-150 opacity-0 scale-[0.96]"
         data-confirm-panel>
      <div class="row gap-3 px-5 pt-5 pb-4 border-b border-(--border)">
        <span class="shrink-0 ${variantColor(variant)}">
          <i data-lucide="${icon}" style="stroke-width: 2; width: 20px; height: 20px;"></i>
        </span>
        <h2 id="confirm-dialog-title" class="t-heading">${title}</h2>
      </div>
      <div class="p-5">
        <p class="t-muted">${message}</p>
      </div>
      <div class="row justify-end gap-3 px-5 pb-5">
        <button type="button" class="${BUTTON_BASE} ${VARIANTS.secondary}" data-confirm-cancel>
          ${cancelLabel}
        </button>
        <button type="button" class="${BUTTON_BASE} ${VARIANTS[variant] || VARIANTS.primary}" data-confirm-submit>
          ${submitLabel}
        </button>
      </div>
    </div>
  `
  return root
}

let openDialog = null

function customConfirm(message, element) {
  return new Promise((resolve) => {
    // Fecha qualquer diálogo anterior (segurança contra empilhamento).
    if (openDialog) {
      openDialog.remove()
      openDialog = null
    }

    const dataset = element?.dataset || {}
    const variant = dataset.confirmVariant === "danger" ? "danger" : "primary"
    const options = {
      title:        dataset.confirmTitle        || "Confirmar ação",
      message,
      submitLabel:  dataset.confirmSubmitLabel  || "Confirmar",
      cancelLabel:  dataset.confirmCancelLabel  || "Cancelar",
      variant,
      icon:         dataset.confirmIcon         || ICONS[variant]
    }

    const dialog = buildDialog(options)
    const previouslyFocused = document.activeElement
    openDialog = dialog

    let settled = false
    const finish = (result) => {
      if (settled) return
      settled = true
      cleanup()
      resolve(result)
    }

    const close = (result) => {
      const panel = dialog.querySelector("[data-confirm-panel]")
      const backdrop = dialog.querySelector("[data-confirm-backdrop]")
      panel.classList.add("opacity-0", "scale-[0.96]")
      backdrop.classList.add("opacity-0")
      setTimeout(() => {
        finish(result)
      }, 150)
    }

    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault()
        close(false)
      } else if (event.key === "Enter") {
        event.preventDefault()
        close(true)
      }
    }

    function cleanup() {
      dialog.remove()
      openDialog = null
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = ""
      previouslyFocused?.focus?.()
    }

    document.addEventListener("keydown", onKey)
    document.body.style.overflow = "hidden"

    dialog.querySelector("[data-confirm-cancel]").addEventListener("click", () => close(false))
    dialog.querySelector("[data-confirm-submit]").addEventListener("click", () => close(true))
    dialog.querySelector("[data-confirm-backdrop]").addEventListener("click", () => close(false))

    document.body.appendChild(dialog)

    // Anima entrada no próximo frame.
    requestAnimationFrame(() => {
      dialog.querySelector("[data-confirm-panel]").classList.remove("opacity-0", "scale-[0.96]")
      dialog.querySelector("[data-confirm-backdrop]").classList.remove("opacity-0")
    })

    // Foco no confirmar; cancelar fica acessível via ESC/backdrop.
    dialog.querySelector("[data-confirm-submit]").focus()
  })
}

Turbo.config.forms.confirm = customConfirm
